import React from "react";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "../../hooks/useTranslation";
import { useThemeMode } from "../../context/ThemeContext";
import { colorForSpeaker } from "../../utils/transcriptionColors";
import {
  getDisplayName,
  getDisplayRole,
} from "../../utils/speakerHelpers";

/**
 * Formata a quantidade de frases atribuídas ao speaker em uma string
 * curta e localizada (ex.: "1 frase", "12 frases"). Se `count` for
 * inválido, devolve string vazia para que o consumidor não renderize
 * a linha de contagem.
 */
function formatSentenceCount(count, t) {
  if (!Number.isFinite(count) || count < 0) return "";
  return count === 1
    ? t("transcription.speakers.sentenceCountSingular")
    : t("transcription.speakers.sentenceCountPlural", { count });
}

/**
 * Apresenta o nome (e papel, quando houver) de um speaker, colorizado
 * conforme a paleta gerada por `transcriptionColors`.
 *
 * Visual reaproveitado tanto na barra de speakers (topo do painel) quanto
 * em cada parágrafo da lista de transcrições.
 *
 * Layout vertical: o NOME aparece na linha superior, com a cor exclusiva
 * do speaker (a cor não é mais representada por uma bolinha — vai direto
 * no texto). O PAPEL, quando presente, aparece logo abaixo, em fonte
 * menor e na cor de texto secundária do sistema.
 *
 * Variantes (`variant`):
 *   - "chip"    : pílula com fundo sutil (na barra de speakers — full size,
 *                 inclui ações de edição). Usada em diálogos e como base.
 *   - "compact" : versão reduzida do chip (fontes e padding menores) para a
 *                 barra de speakers atual, onde o objetivo é só identificar
 *                 rapidamente os participantes sem ocupar muito espaço.
 *   - "inline"  : sem fundo (uso compacto, no parágrafo da transcrição e
 *                 no menu de troca de speaker).
 *
 * Props:
 *   - `speaker`        objeto { speaker_id, name, role } — pode ser null/undefined
 *                      (nesse caso renderiza um placeholder neutro).
 *   - `speakers`       lista completa de speakers da transcrição (necessária
 *                      para resolver a cor consistente).
 *   - `variant`        "chip" | "inline" (default: "chip")
 *   - `selected`       quando true, aplica destaque (borda mais forte).
 *   - `onClick`        torna o badge clicável (cursor pointer).
 *   - `compactRole`    se true, esconde o papel quando não houver espaço.
 *   - `endAdornment`   nó React opcional para colocar à direita do nome.
 *   - `sentenceCount`  número opcional. Quando passado, exibe um contador
 *                      discreto de frases logo abaixo do papel — útil na
 *                      barra de speakers para mostrar "quantas falas".
 */
function SpeakerBadge({
  speaker,
  speakers,
  variant = "chip",
  selected = false,
  onClick,
  compactRole = false,
  endAdornment,
  sentenceCount,
  className,
  style,
}) {
  const { t } = useTranslation("common");
  const { themeMode } = useThemeMode();
  const palette = colorForSpeaker(
    speaker?.speaker_id,
    speakers,
    themeMode,
  );
  const unidentifiedLabel = t("transcription.speakers.unidentified");
  const name = getDisplayName(speaker, unidentifiedLabel);
  const role = getDisplayRole(speaker);

  const baseClass = [
    "transcription-speaker-badge",
    `transcription-speaker-badge--${variant}`,
    selected ? "is-selected" : "",
    onClick ? "is-clickable" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  /* Cores são aplicadas inline porque dependem do speaker e do tema; outras
     propriedades visuais ficam em `App.css` (transcription-speaker-badge*).
     Tanto `chip` quanto `compact` usam fundo/borda do palette; `inline`
     fica sem fundo (cor só no nome). */
  const badgeStyle =
    variant === "chip" || variant === "compact"
      ? {
          backgroundColor: palette.surface,
          borderColor: selected ? palette.color : palette.border,
          ...style,
        }
      : { ...style };

  const handleClick = onClick
    ? (e) => {
        e.stopPropagation();
        onClick(e);
      }
    : undefined;

  return (
    <Box
      className={baseClass}
      onClick={handleClick}
      style={badgeStyle}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <Box className="transcription-speaker-badge__labels">
        <Typography
          component="span"
          className="transcription-speaker-badge__name"
          style={{ color: palette.color }}
        >
          {name}
        </Typography>
        {role && !compactRole && (
          <Typography
            component="span"
            className="transcription-speaker-badge__role"
          >
            {role}
          </Typography>
        )}
        {Number.isFinite(sentenceCount) ? (
          <Typography
            component="span"
            className="transcription-speaker-badge__count"
          >
            {formatSentenceCount(sentenceCount, t)}
          </Typography>
        ) : null}
      </Box>
      {endAdornment ? (
        <Box className="transcription-speaker-badge__end">{endAdornment}</Box>
      ) : null}
    </Box>
  );
}

/* `React.memo`: o badge é renderizado em CADA parágrafo da transcrição
   (potencialmente centenas) e dentro do menu de troca de speaker. Sem
   memoização, qualquer re-render do parágrafo recomputa cor + estilos.
   Como as props que mudam de fato (speaker, speakers, variant, ...) já
   são estáveis quando o conteúdo não muda, a memoização padrão (shallow
   compare) é suficiente. */
export default React.memo(SpeakerBadge);
